import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { CONTACT_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useCreateContact from "@/hooks/requests/contact/use-create-contact";
import useCreateTag from "@/hooks/requests/tags/use-create-tag";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useLoading from "@/hooks/use-loading";
import IContact from "@/interfaces/contact.interface";
import { ITagInput } from "@/interfaces/tag.interface";
import { ContactInfoMessages } from "@/messages/contacts/contact-Info.messages";

import AddressSection from "./components/addressSection";
import ContactInfo from "./components/contactInfo";
import Tags from "./components/tags";
import useUpdateContact from "../../../../../hooks/requests/contact/use-update-contact";
import { buildFormSchema, buildInitialData, normalizeContactFormData } from "../../form-data";
import { ContactFormData } from "../../types";
import { AdditionalAddresses } from "../AdditionalAddresses";

type Props = {
  messages: ContactInfoMessages;
  contact: IContact | null;
  isEditing: boolean | undefined;
};

export default function PersonalDetailsTab({ messages, contact, isEditing }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();

  const { update } = useUpdateContact();
  const { create } = useCreateContact();
  const { create: createTag } = useCreateTag();
  const { isLoading, startLoading, endLoading } = useLoading();
  const hasNoPermissionToEdit = useHasNoPermission(CONTACT_PERMISSIONS.EDIT);

  const onSuccess = () => router(ROUTE_URLS.contacts(lang));
  const onError = () => endLoading();

  const handleUpdate = async (_formData: ContactFormData) => {
    if (!user) return;

    startLoading();

    try {
      const newTagPromises = _formData.newTags.map((tagName) => {
        const formData: ITagInput = { name: tagName };
        const successMessage = messages.tagCreateSuccess;
        const errorMessage = messages.tagCreateError;

        return createTag({ formData, successMessage, errorMessage, onError });
      });

      await Promise.all(newTagPromises);

      const normalizeData = normalizeContactFormData(_formData, user);

      const successMessage = messages.contactUpdateSuccess;
      const errorMessage = messages.contactUpdateError;
      const _id = Number(contact?.id) || 0;
      const formData = { ...contact, ...normalizeData };

      await update({ _id, formData, successMessage, errorMessage, onSuccess, onError });
    } catch (error) {
      endLoading();
    }
  };

  const handleCreate = async (_formData: ContactFormData) => {
    if (!user) return;
    startLoading();

    try {
      const newTagPromises = _formData.newTags.map((tagName: string) => {
        const formData: ITagInput = { name: tagName };
        const successMessage = messages.tagCreateSuccess;
        const errorMessage = messages.tagCreateError;

        return createTag({ formData, successMessage, errorMessage });
      });

      await Promise.all(newTagPromises);

      const formData = normalizeContactFormData(_formData, user);
      const successMessage = messages.contactCreateSuccess;
      const errorMessage = messages.contactCreateError;

      await create({ successMessage, formData, errorMessage, onSuccess, onError });
    } catch (error) {
      endLoading();
    }
  };

  const handleSubmitForm = async (_formData: ContactFormData) => {
    if (!user) return;
    try {
      if (isEditing) {
        await handleUpdate(_formData);
      } else {
        await handleCreate(_formData);
      }
    } catch (error) {
      endLoading();
    }
  };

  return (
    <Formik
      initialValues={buildInitialData(contact)}
      validationSchema={buildFormSchema(messages)}
      validateOnMount={true}
      validateOnBlur={true}
      onSubmit={handleSubmitForm}
    >
      {({ isSubmitting, isValid }) => {
        return (
          <Form>
            <MDBox mt={2}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 8 }}>
                  <MDBox height="100%">
                    <ContactInfo messages={messages} isEditing={isEditing} />
                  </MDBox>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                  <MDBox height="100%">
                    <Tags messages={messages} />
                  </MDBox>
                </Grid>

                <Grid size={{ xs: 12, lg: 12 }}>
                  <AddressSection messages={messages} />
                </Grid>

                {!!isEditing && contact && (
                  <Grid size={{ xs: 12, lg: 12 }}>
                    <AdditionalAddresses contact={contact} messages={messages} />
                  </Grid>
                )}
              </Grid>

              <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="light"
                  onClick={() => router(ROUTE_URLS.contacts(lang))}
                  isLoading={isLoading}
                >
                  {messages.cancelButton}
                </MDButton>

                <MDButton
                  variant="gradient"
                  color="dark"
                  type="submit"
                  isLoading={isLoading || isSubmitting}
                  disabled={hasNoPermissionToEdit || !isValid}
                >
                  {messages.updateButton}
                </MDButton>
              </MDBox>
            </MDBox>
          </Form>
        );
      }}
    </Formik>
  );
}
